import { Injector } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { config } from '../../../config/config';
import { cgpCookieHelper } from '../../../modules/cgp-cookie-helper';
import { CgpAlertDialogService } from '../cgp-alert-dialog/cgp-alert-dialog.service';
import { cgpAuthentication } from '../../../modules/cgp-authentication';
import { HttpClient, HttpParams, HttpHeaders, HttpErrorResponse } from '@angular/common/http';
import { Observable } from 'rxjs';
import { Router } from '@angular/router';

const HEADER_DEFAULT = 'application/json';
export abstract class BaseHttpService extends BehaviorSubject<any> {
    private http: HttpClient;
    private router: Router;
    private cgpAlertDialogService: CgpAlertDialogService;
    loading: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    filteredData: BehaviorSubject<any[]> = new BehaviorSubject<any[]>([]);
    minimumFilterLength: number = 0;
    private checkCount: number = 0;

    constructor(private readonly injector: Injector) {
        super(null);
        this.http = this.injector.get(HttpClient);
        this.router = this.injector.get(Router);
        this.cgpAlertDialogService = this.injector.get(CgpAlertDialogService);
        this.subscribe(x => {
            this.resetFilter();
            this.loading.next(false);
        });
    }


    /**
     * 
     */
    resetFilter() {
        try {
            this.checkCount = 0;
            this.updateFilteredData();
        } catch (e) { }
    }


    updateFilteredData(data: any[] = this.value) {
        if (this.checkCount >= this.minimumFilterLength) {
            if (data) {
                if (data instanceof Array) {
                    this.filteredData.next(data);
                } else {
                    this.filteredData.next([data]);
                }
            }
        }
    }


    /**
     * 
     */
    handleFilter(check: string, field: string = undefined, selected: any[] = []): void {
        let result: any[] = this.value;
        try {
            if (check && check !== '') {
                this.checkCount = this.checkCount + 1;
                result = this.value.filter(x => {
                    const source: string = field ? x[field].toString() : x.toString();
                    return source.toLowerCase().indexOf(check.toLowerCase()) !== -1;
                });
            }
        } catch (e) { }

        if (check && check !== '' && selected instanceof Array) {
            result = result.filter(x => !selected.some(s => {
                const source = x.Id || x;
                const check = s.Id || s;
                return source === check;
            }));
        }
        this.updateFilteredData(result);
    }


    /**
     * 
     */
    handleError = (error: HttpErrorResponse) => {
        this.loading.next(false);
        if (error.status >= 200 && error.status < 400) return;
        const isUnauthorized: boolean = error.status == 401;
        const _unauthMsg = ". Click OK to refresh token";
        const _body = this.getErrorBody(error) + (isUnauthorized ? _unauthMsg : "");
        const _status = `error.status  ${error.statusText}`;
        if (this.cgpAlertDialogService.isOpen()) return;
        this.cgpAlertDialogService.showAlertDialog({
            title: `HTTP Error: ${_status}`,
            message: _body,
            alert: 'danger',
            closeLabel: 'OK',
            onClosed: () => isUnauthorized ? this.refreshToken() : null,
        });
    }


    /**
     * 
     * @param url 
     * @param catchError 
     */
    protected fetch(url, catchError = true): Promise<any> {
        return this.handleResponse(this.http.get(url, this.getOptions()), catchError);
    }


    /**
     * 
     * @param body 
     * @param url 
     * @param catchError 
     */
    protected post(body: any, url: string, catchError = true, searchParams: HttpParams = null): Promise<any> {
        return this.handleResponse(this.http.post(url, JSON.stringify(body), { headers: this.getHeaders(), params: searchParams, observe: "response" }), catchError);
    }

    /**
 * 
 * @param body 
 * @param url 
 * @param catchError 
 */
    protected upload(body: any, url: string, catchError = true, searchParams: HttpParams = null, boundary = null): Promise<any> {
        this.loading.next(true);
        const token: string = this.getToken();

        const headers = new HttpHeaders()
            .set('Accept', `multipart/form-data; boundary=${boundary}`)
            .set('Authorization', token);
        return this.handleResponse(this.http.post(url, body, { headers }), catchError);
    }



    /**
     * 
     * @param body 
     * @param url 
     * @param catchError 
     */
    protected put(body: any, url: string, catchError = true): Promise<any> {
        return this.handleResponse(this.http.put(url, JSON.stringify(body), this.getOptions()), catchError);
    }


    /**
     * 
     * @param url 
     * @param catchError 
     */
    protected delete(url: string, catchError = true): Promise<any> {
        return this.handleResponse(this.http.delete(url, this.getOptions()), catchError);
    }


    /**
     * 
     * @param url 
     */
    protected download(body: any, url: string, searchParams: HttpParams = null, catchError = true): Promise<any> {
        const headers = this.getHeaders();
        const options = {
            headers: headers,
            params: searchParams,
            observe: "response",
            responseType: "arraybuffer"
        } as any;

        return this.handleResponse(this.http.post(url, JSON.stringify(body), options), catchError);
    }


    /**
     * 
     * @param data 
     * @param url 
     */
    protected uploadS3Object(data: any, url: string): Promise<any> {
        return this.http.post(url, data, { headers: this.getS3Headers() })
            .toPromise()
            .catch(this.handleError);
    }


    /**
     * 
     * @param data 
     * @param url 
     */
    protected copyS3Object(data: any, url: string): Promise<any> {
        const _headers: HttpHeaders = this.getS3Headers(true)
            .set('Content-Type', HEADER_DEFAULT + '; charset=utf-8')
        return this.http.post(url, data, { headers: _headers })
            .toPromise()
            .catch(this.handleError);
    }


    /**
     * 
     * @param url 
     */
    protected removeS3Object(url: string): Promise<any> {
        return this.http.delete(url, { headers: this.getS3Headers() })
            .toPromise()
    }


    /**
     * 
     * @param url 
     */
    protected getS3Object(url: string): Promise<any> {
        return this.http.get(url, { headers: this.getS3Headers(), responseType: "blob" })
            .toPromise();
    }


    /**
      * 
      */
    private getS3Headers(isJson = false): HttpHeaders {
        const token: string = cgpCookieHelper
            .retrieveCookie(config.authentication.authenticationCookie)
            .replace('Bearer_', 'Bearer ');
        const _accept = isJson ? HEADER_DEFAULT : 'application/octet-stream';
        return new HttpHeaders()
            .set('Accept', _accept)
            .set('Authorization', token)
            .set('appId', 'mesc2')
    }


    /**
     * 
     */
    private getHeaders() {
        try {
            this.loading.next(true);
            const token: string = this.getToken();

            const headers = new HttpHeaders({ 'Content-Type': HEADER_DEFAULT })
                .set('Accept', HEADER_DEFAULT)
                .set('TimeZoneOffset', new Date().getTimezoneOffset().toString())
                .set('Authorization', token)
                .set('Cache-Control', 'no-cache')
                .set('Pragma', 'no-cache');
            return headers;
        } catch (e) { }
    }


    /**
     * 
     */
    private getOptions(): any {
        return { withCredentials: true, headers: this.getHeaders(), observe: "response" } as any;
    }


    /**
     * 
     */
    private getToken(): string {
        const _token = cgpCookieHelper.retrieveCookie(config.authentication.authenticationCookie);
        return _token.replace('Bearer_', 'Bearer ');
    }


    /**
     * 
     * @param headers 
     */
    private updateToken(headers: any) {
        try {
            //let newToken = headers.get(config.authentication.authenticationCookie);

            //newToken = newToken.replace('Bearer ', '');
            //cgpCookieHelper.setAuthCookie(newToken);
        } catch (e) { }
    }


    /**
     * 
     */
    private refreshToken = () => {
        this.router.navigate(['/'])
        cgpAuthentication.refreshToken(null)
        window.location.reload();
    }


    /**
     * 
     * @param response 
     * @param catchError 
     */
    private handleResponse(response: Observable<any>, catchError = true): Promise<any> {
        const responsePromise: Promise<any> = response
            .toPromise()
            .then(x => {
                try {
                    this.updateToken(x.headers);
                    this.next(x.body);
                    return x.body;
                } catch (e) {
                    return x;
                }
            });
        return catchError ? responsePromise
            .catch(e => this.handleError(e)) : responsePromise;
    }


    /**
     * 
     * @param error 
     */
    private getErrorBody(error: any): string {
        const _err = error.error ? error.error : error;
        if (typeof (_err) === typeof ({})) {
            const errors: any[] = [];
            for (const it in _err) {
                const e = _err[it].Errors
                if (e && e[0]) {
                    errors.push(e[0].ErrorMessage);
                }
            }
            return errors.join('\n');
        }
        return _err;
    }

}
